<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="state-wrap">Loading role form...</div>
            <div v-else>
              <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

              <form @submit.prevent="submitForm" class="access-form">
                <div class="row g-3">
                  <div class="col-lg-4 col-sm-12">
                    <label class="form-label">Role Name</label>
                    <input v-model="form.rolename" type="text" class="form-control" required />
                  </div>
                  <div class="col-lg-6 col-sm-12">
                    <label class="form-label">Description</label>
                    <input v-model="form.description" type="text" class="form-control" />
                  </div>
                  <div class="col-lg-2 col-sm-12">
                    <label class="form-label d-block">Active</label>
                    <input v-model="form.is_active" type="checkbox" />
                  </div>
                </div>

                <PermissionMatrix
                  v-model="selectedPermissionIds"
                  :permissions="permissionGroups"
                  title="Role Permissions"
                />

                <div class="form-actions">
                  <button class="btn btn-submit" type="submit" :disabled="submitting">
                    {{ submitting ? 'Saving...' : (isEdit ? 'Update Role' : 'Create Role') }}
                  </button>
                  <router-link class="btn btn-cancel" to="/grouppermissions">Cancel</router-link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

import PermissionMatrix from '@/components/access/PermissionMatrix.vue';
import { groupPermissionsByModule } from '@/utils/accessControl';

export default {
  name: 'RoleFormPage',
  components: { PermissionMatrix },
  props: {
    mode: {
      type: String,
      default: 'create',
    },
  },
  data() {
    return {
      loading: true,
      submitting: false,
      errorMessage: '',
      permissions: [],
      selectedPermissionIds: [],
      form: {
        rolename: '',
        description: '',
        is_active: true,
      },
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'edit';
    },
    roleId() {
      return this.$route.query.id || '';
    },
    title() {
      return this.isEdit ? 'Edit Role' : 'Create Role';
    },
    title1() {
      return this.isEdit ? 'Update role permissions' : 'Create a reusable permission set';
    },
    permissionGroups() {
      return groupPermissionsByModule(this.permissions);
    },
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.errorMessage = '';

      try {
        const requests = [axios.get('/v1/access/permissions')];
        if (this.isEdit && this.roleId) {
          requests.push(axios.get(`/v1/access/roles/${this.roleId}`));
        }

        const [permissionsRes, roleRes] = await Promise.all(requests);
        this.permissions = permissionsRes.data || [];

        if (roleRes?.data) {
          const role = roleRes.data;
          this.form = {
            rolename: role.rolename || '',
            description: role.description || '',
            is_active: !!role.is_active,
          };
          this.selectedPermissionIds = (role.permissions || []).map((permission) => String(permission.permissionid));
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to load role form.';
      } finally {
        this.loading = false;
      }
    },
    async submitForm() {
      this.submitting = true;
      try {
        const payload = {
          rolename: this.form.rolename,
          description: this.form.description,
          is_active: this.form.is_active,
          permission_ids: this.selectedPermissionIds,
        };

        if (this.isEdit) {
          await axios.put(`/v1/access/roles/${this.roleId}`, payload);
        } else {
          await axios.post('/v1/access/roles', payload);
        }

        Swal.fire({
          icon: 'success',
          title: this.isEdit ? 'Role updated' : 'Role created',
          timer: 1400,
          showConfirmButton: false,
        });
        this.$router.push('/grouppermissions');
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Save failed',
          text: error.response?.data?.detail || 'Could not save role.',
        });
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.state-wrap { padding:24px; color:#6b7280; }
.access-form { display:flex; flex-direction:column; gap:20px; }
.form-actions { display:flex; gap:12px; flex-wrap:wrap; }
</style>
